import {
  Component,
  Input,
  OnInit,
  Inject,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {ReviewService} from '../../../services/review.service';
import {getCountryCode, getCountryName} from '../../../../../shared/utils/countries';
import {DivisionsService} from '../../../../../shared/modules/core/services/divisions.service';
import {CookieService} from 'ngx-cookie-service';

@Component({
  selector: 'app-service-reviews',
  templateUrl: './service-reviews.component.html',
  styleUrls: ['./service-reviews.component.scss']
})
export class ServiceReviewsComponent implements OnInit {
  @Input() service;
  @Input() game;
  carouselInitialized = false;
  @Input('current') set _current(val: number) {
    this.current = val;

    if (this.ratingResp) {
      this.calculateCustomerCount();
      this.calculateRating();
    }

    if (this.service && this.service !== 'normalgames') {
      const rankName = DivisionsService.getRankByValue(val, this.game);
      this.rankName = this.service === 'placements' && rankName
        ? rankName.split(' ')[0]
        : rankName;
    }
  }

  currentSlide: number = 0;
  autoPlayInterval: any; // To store our interval

  startAutoPlay() {
    // Clear any existing interval
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
    }

    // Set the carousel to change slides every 5 seconds
    this.autoPlayInterval = setInterval(() => {
      this.nextSlide();
    }, 5000);
  }

  stopAutoPlay() {
    // Clear the interval
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
    }
  }

  nextSlide() {
    if(this.currentSlide < this.reviews.length - 1) {
      this.currentSlide++;
    } else {
      this.currentSlide = 0;  // Go back to the first slide
    }
    if (this.isBrowser) {
      this.startAutoPlay();
    }
  }

  prevSlide() {
    if(this.currentSlide > 0) {
      this.currentSlide--;
    } else {
      this.currentSlide = this.reviews.length - 1;  // Jump to the last slide
    }
    if (this.isBrowser) {
      this.startAutoPlay();
    }
  }

  countryUpdate(country) {
    let cookieCountry;

    if (country === 'Great Britain') {
      country = 'United Kingdom';
    }

    if (this.cookie.check('gg-country')) {
      cookieCountry = this.cookie.get('gg-country');
      if (cookieCountry === 'US') {
        return 'United States of America';
      } else if (country === 'United States of America') {
        return getCountryName(cookieCountry);
      }
    }
    return country;
  }

  @Input() type;
  @ViewChild('owlCar') owlCar;
  public reviews;
  public customerCount;
  public rating;
  public getCC = getCountryCode;
  public customerCountry = '';
  private ratingResp: any;
  public rankName;
  public reviewsLoaded = false;

  customOptions: any = {
    loop: true,
    mouseDrag: true,
    touchDrag: true,
    pullDrag: false,
    dots: false,
    autoHeight: true,
    autoplay: true,
    autoplayTimeout: 5000,
    autoplaySpeed: 500,
    navSpeed: 500,
    navText: ['', ''],
    responsive: {
      0: {
        items: 1
      },
      400: {
        items: 1
      },
      740: {
        items: 1
      },
      940: {
        items: 1
      }
    },
    nav: true
  };

  public isBrowser = false;
  private current;

  public constructor(private reviewService: ReviewService, private cookie: CookieService, @Inject(PLATFORM_ID) platformId) {
    if (isPlatformBrowser(platformId)) {
      this.isBrowser = true;
    }
  }

  updateReviews(reviews) {
    for (let i = 0; i < reviews.length; i++) {
      reviews[i].createdAt = this.createRandDate();
      if (this.getRandomInt(3) === 0) {
        reviews[i].order.customer.country = this.countryUpdate(reviews[i].order.customer.country);
      }
      reviews[i].order.customer.countryCC = this.getCC(reviews[i].order.customer.country);
    }
  }

  getRandomInt(max) {
    return Math.floor(Math.random() * max);
  }

  createRandDate() {
    const dayNumber = this.getRandomInt(4);

    if (dayNumber === 0) {
      const hours = this.getRandomInt(22) + 2;
      return hours + ' hours';
    } else {
      return dayNumber === 1 ? '1 day' : dayNumber + ' days';
    }
  }

  async ngOnInit() {

    if (this.isBrowser) {
      this.startAutoPlay();
    }

    if (this.cookie.check('gg-country')) {
      this.customerCountry = this.cookie.get('gg-country');
    } else {
      const country = await this.reviewService.getCountry().toPromise();
      if (country) {
        if (country.countryCode) {
          this.customerCountry = country.countryCode;
          this.cookie.set('gg-country', country.countryCode, 3650, '/');
        }
      }
    }

    if (!this.reviewsLoaded) {
      const resp = await this.reviewService.getReviews().toPromise();
      if (resp) {
        this.reviews = this.shuffle(resp.reviews);
        this.updateReviews(this.reviews);
        this.reviewsLoaded = true;
      }
    }
  }

  ngOnDestroy() {
    this.stopAutoPlay();
  }

  shuffle<T>(array: T[]): T[] {
    let currentIndex = array.length;
    let randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex !== 0) {

      // Pick a remaining element.
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex], array[currentIndex]];
    }

    return array;
  }

  calculateRating() {
    let rating;

    if (this.service === 'normalgames') {
      rating = this.ratingResp[this.game === 'tft' ? 'tftSoloNormalgames' : 'soloNormalgames'].rates;
    } else {
      rating = this.ratingResp[(
        this.game === 'tft'
          ? ('tft' + this.capitalizeFirstLetter(this.type))
          : this.type
      ) + this.capitalizeFirstLetter(this.service)].rates[this.current];
    }

    this.rating = rating;
  }

  calculateCustomerCount() {
    let customerCount;

    if (this.service === 'normalgames') {
      customerCount = this.ratingResp[this.game === 'tft' ? 'tftSoloNormalgames' : 'soloNormalgames'].customers;
    } else {
      customerCount = this.ratingResp[
      (
        this.game === 'tft'
          ? ('tft' + this.capitalizeFirstLetter(this.type))
          : this.type
      )
      + this.capitalizeFirstLetter(this.service)].customers[this.current];
    }

    this.customerCount = customerCount;
  }

  onNext() {
    if (this.owlCar) {
      this.owlCar.next();
    }
  }

  onPrev() {
    if (this.owlCar) {
      this.owlCar.prev();
    }
  }

  capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
